import {Uint64} from "@cosmjs/math";
import {EncodeObject, GeneratedType} from "@shareledgerjs/signing";
import {Coin} from "@shareledgerjs/types/cosmos/base/v1beta1/coin";
import {
  MsgDeposit as V1MsgDeposit,
  MsgSubmitProposal as V1MsgSubmitProposal,
  MsgUpdateParams as V1MsgUpdateParams,
  MsgVote as V1MsgVote,
  MsgVoteWeighted as V1MsgVoteWeighted
} from "@shareledgerjs/types/cosmos/gov/v1/tx";
import {TextProposal, VoteOption} from "@shareledgerjs/types/cosmos/gov/v1beta1/gov";
import {MsgDeposit, MsgSubmitProposal, MsgVote, MsgVoteWeighted} from "@shareledgerjs/types/cosmos/gov/v1beta1/tx";
import {Any} from "@shareledgerjs/types/google/protobuf/any";
import Long from "long";

import {BaseClient} from "../../baseclient";
import {longify} from "../../query";

export type GovProposalId = string | number | Long | Uint64;

export function createGovTypes(): ReadonlyArray<[string, GeneratedType]> {
  return [
    ["/cosmos.gov.v1.MsgDeposit", V1MsgDeposit],
    ["/cosmos.gov.v1.MsgSubmitProposal", V1MsgSubmitProposal],
    ["/cosmos.gov.v1.MsgUpdateParams", V1MsgUpdateParams],
    ["/cosmos.gov.v1.MsgVote", V1MsgVote],
    ["/cosmos.gov.v1.MsgVoteWeighted", V1MsgVoteWeighted],
    ["/cosmos.gov.v1beta1.MsgSubmitProposal", MsgSubmitProposal],
    ["/cosmos.gov.v1beta1.MsgDeposit", MsgDeposit],
    ["/cosmos.gov.v1beta1.MsgVote", MsgVote],
    ["/cosmos.gov.v1beta1.MsgVoteWeighted", MsgVoteWeighted]
  ];
}

export function createGovActions(): Record<string, string> {
  return {
    "/cosmos.gov.v1beta1.MsgSubmitProposal": "gov_submit-proposal",
    "/cosmos.gov.v1beta1.MsgDeposit": "gov_deposit",
    "/cosmos.gov.v1beta1.MsgVote": "gov_vote"
  };
}

export interface TextProposalEncodeObject extends EncodeObject {
  readonly typeUrl: "/cosmos.gov.v1beta1.TextProposal";
  readonly value: Partial<TextProposal>;
}

export function isMsgTextProposalEncodeObject(encodeObject: EncodeObject): encodeObject is TextProposalEncodeObject {
  return (encodeObject as TextProposalEncodeObject).typeUrl === "/cosmos.gov.v1beta1.TextProposal";
}

export interface MsgSubmitProposalEncodeObject extends EncodeObject {
  readonly typeUrl: "/cosmos.gov.v1beta1.MsgSubmitProposal";
  readonly value: Partial<MsgSubmitProposal>;
}

export function isMsgSubmitProposalEncodeObject(encodeObject: EncodeObject): encodeObject is MsgSubmitProposalEncodeObject {
  return (encodeObject as MsgSubmitProposalEncodeObject).typeUrl === "/cosmos.gov.v1beta1.MsgSubmitProposal";
}

export interface MsgVoteEncodeObject extends EncodeObject {
  readonly typeUrl: "/cosmos.gov.v1beta1.MsgVote";
  readonly value: Partial<MsgVote>;
}

export function isMsgVoteEncodeObject(encodeObject: EncodeObject): encodeObject is MsgVoteEncodeObject {
  return (encodeObject as MsgVoteEncodeObject).typeUrl === "/cosmos.gov.v1beta1.MsgVote";
}

export interface MsgVoteWeightedEncodeObject extends EncodeObject {
  readonly typeUrl: "/cosmos.gov.v1beta1.MsgVoteWeighted";
  readonly value: Partial<MsgVoteWeighted>;
}

export function isMsgVoteWeightedEncodeObject(encodeObject: EncodeObject): encodeObject is MsgVoteWeightedEncodeObject {
  return (encodeObject as MsgVoteWeightedEncodeObject).typeUrl === "/cosmos.gov.v1beta1.MsgVoteWeighted";
}

// There are no EncodeObject implementations for the new v1 message types because
// those things don't scale (https://github.com/cosmos/cosmjs/issues/1440). We need to
// address this more fundamentally. Users can use
// const msg = {
//   typeUrl: "/cosmos.gov.v1.MsgDeposit",
//   value: MsgDeposit.fromPartial({ ... })
// }
// in their app.
export interface MsgDepositEncodeObject extends EncodeObject {
  readonly typeUrl: "/cosmos.gov.v1beta1.MsgDeposit";
  readonly value: Partial<MsgDeposit>;
}

export function isMsgDepositEncodeObject(encodeObject: EncodeObject): encodeObject is MsgDepositEncodeObject {
  return (encodeObject as MsgDepositEncodeObject).typeUrl === "/cosmos.gov.v1beta1.MsgDeposit";
}

export interface GovTxExtensionMethods {
  submitProposal(proposer: string, initialDeposit: Coin[], content?: {title: string; description: string}): MsgSubmitProposalEncodeObject;
  voteTx(proposalId: GovProposalId, voter: string, option: VoteOption): MsgVoteEncodeObject;
  voteWeightedTx(
    proposalId: GovProposalId,
    voter: string,
    options: Array<{option: VoteOption; weight: string}>
  ): MsgVoteWeightedEncodeObject;
  depositTx(proposalId: GovProposalId, depositor: string, amount: Coin[]): MsgDepositEncodeObject;
}

export interface GovTxExtension {
  readonly gov: GovTxExtensionMethods;
}

export function GovTxExtension<T extends {new (...args: any[]): BaseClient & GovTxExtension}>(constructor: T): T {
  return class extends constructor {
    override get gov() {
      return {
        ...super["gov"],
        submitProposal: (
          proposer: string,
          initialDeposit: Coin[],
          content?: {title: string; description: string}
        ): MsgSubmitProposalEncodeObject => {
          return {
            typeUrl: "/cosmos.gov.v1beta1.MsgSubmitProposal",
            value: MsgSubmitProposal.fromPartial({
              proposer,
              initialDeposit: [...initialDeposit],
              content: content
                ? Any.fromPartial({
                    typeUrl: "/cosmos.gov.v1beta1.TextProposal",
                    value: Uint8Array.from(
                      TextProposal.encode(
                        TextProposal.fromPartial({
                          title: content.title,
                          description: content.description
                        })
                      ).finish()
                    )
                  })
                : undefined
            })
          };
        },
        voteTx: (proposalId: GovProposalId, voter: string, option: VoteOption): MsgVoteEncodeObject => {
          return {
            typeUrl: "/cosmos.gov.v1beta1.MsgVote",
            value: MsgVote.fromPartial({
              proposalId: longify(proposalId),
              voter,
              option
            })
          };
        },
        voteWeightedTx: (
          proposalId: GovProposalId,
          voter: string,
          options: Array<{option: VoteOption; weight: string}>
        ): MsgVoteWeightedEncodeObject => {
          return {
            typeUrl: "/cosmos.gov.v1beta1.MsgVoteWeighted",
            value: MsgVoteWeighted.fromPartial({
              proposalId: longify(proposalId),
              voter,
              options
            })
          };
        },
        depositTx: (proposalId: GovProposalId, depositor: string, amount: Coin[]): MsgDepositEncodeObject => {
          return {
            typeUrl: "/cosmos.gov.v1beta1.MsgDeposit",
            value: MsgDeposit.fromPartial({
              proposalId: longify(proposalId),
              depositor,
              amount: [...amount]
            })
          };
        }
      };
    }
  };
}
